import type { PostApiV1ExamQuestionLibraryAiCorrectionMutationRequest } from '@/shared/api/generated/types';

import type { QuestionAlternative } from '../hooks/useQuestionEdit';

/**
 * Dados do formulário necessários para montar o payload de revisão com IA.
 */
export interface AiReviewRequestInput {
  /** ID do tipo de componente da questão (1=SingleChoice, 2=MultipleChoice, …). */
  readonly componentType: number;
  /** Código de cultura/idioma (ex.: `pt-BR`). */
  readonly cultureInfo: string | undefined;
  /** Enunciado (pode conter HTML). */
  readonly label: string | undefined;
  /** Informação complementar (pode conter HTML). */
  readonly information?: string | undefined;
  /** ID do autor da questão. */
  readonly personAuthorId: number | undefined;
  /** Peso da questão. */
  readonly weight?: number | null | undefined;
  /** Nível de dificuldade. */
  readonly level?: number | null | undefined;
  /** ID da disciplina. */
  readonly disciplineId?: number | null | undefined;
  /** ID da empresa. */
  readonly companyId?: number | null | undefined;
  /** Indica se a questão é de escolha (envia alternativas). */
  readonly isChoiceType: boolean;
  /** Alternativas atuais do formulário. */
  readonly alternatives: readonly QuestionAlternative[];
  /** IDs (de UI) das alternativas marcadas como corretas. */
  readonly correctAlternativeIds: readonly string[];
  /**
   * Subtipo dissertativo selecionado (`EExamItemSubtype`). Define a estratégia de
   * revisão aplicada pela IA. Null quando o tipo não aceita subtipo.
   */
  readonly subtype?: number | null | undefined;
  /** Resposta-modelo / Notas de Ensino (obrigatória nos subtipos 3 e 4). */
  readonly modelAnswer?: string | null | undefined;
  /** Termos-chave esperados na resposta (subtipo 3). */
  readonly keyTerms?: string | null | undefined;
}

/**
 * Converte o id de UI de uma alternativa para o id numérico esperado pelo backend.
 * O contrato do endpoint usa `0` para alternativas novas; existentes usam `originalId`.
 */
function toBackendAlternativeId(alt: QuestionAlternative): number {
  return alt.originalId ?? 0;
}

/**
 * Monta o payload de `POST /api/v1/exam/question-library/ai-correction` a partir do
 * estado atual do formulário de questão.
 *
 * Para tipos de escolha (Escolha Simples/Múltipla) inclui as alternativas em
 * `ExamTypeList.ExamTypeListValues` com o texto e a marcação de resposta correta.
 *
 * Para questões dissertativas com subtipo (`MultilineText`) inclui `Subtype` e,
 * quando preenchidos, `ModelAnswer` e `KeyTerms` — é o subtipo que determina a
 * estratégia de revisão aplicada pela IA no backend.
 *
 * @param input - Valores atuais do formulário relevantes para a revisão
 * @returns Payload no formato esperado pelo cliente gerado pelo Kubb
 *
 * @example
 * ```ts
 * const request = buildAiReviewRequest({
 *   componentType: 2,
 *   cultureInfo: 'pt-BR',
 *   label: '<p>Enunciado…</p>',
 *   personAuthorId: 1,
 *   isChoiceType: true,
 *   alternatives,
 *   correctAlternativeIds: ['0'],
 * });
 * ```
 */
export function buildAiReviewRequest(
  input: AiReviewRequestInput,
): PostApiV1ExamQuestionLibraryAiCorrectionMutationRequest {
  const {
    componentType,
    cultureInfo,
    label,
    information,
    personAuthorId,
    weight,
    level,
    disciplineId,
    companyId,
    isChoiceType,
    alternatives,
    correctAlternativeIds,
    subtype,
    modelAnswer,
    keyTerms,
  } = input;

  return {
    ComponentType: componentType,
    CultureInfo: cultureInfo ?? 'pt-BR',
    Label: label ?? '',
    PersonAuthorId: personAuthorId ?? 0,
    ...(information ? { Information: information } : {}),
    ...(weight != null ? { Weight: weight } : {}),
    ...(level != null ? { Level: level } : {}),
    ...(disciplineId != null ? { DisciplineId: disciplineId } : {}),
    ...(companyId != null ? { CompanyId: companyId } : {}),
    ...(subtype != null ? { Subtype: subtype } : {}),
    ...(modelAnswer ? { ModelAnswer: modelAnswer } : {}),
    ...(keyTerms ? { KeyTerms: keyTerms } : {}),
    ...(isChoiceType
      ? {
          'ExamTypeList.ExamTypeListValues': alternatives.map(alt => ({
            id: toBackendAlternativeId(alt),
            value: alt.value,
            correctAnswer: correctAlternativeIds.includes(alt.id),
          })),
        }
      : {}),
  };
}
